import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** CRA는 REACT_APP_ 접두사 변수만 클라이언트에 주입함 */
const KAKAO_APP_KEY = process.env.REACT_APP_KAKAO_MAP_APP_KEY;

/** 카카오 주소 검색용*/
const GEOCODE_ADDRESS_KO = '서울특별시 강남구 도산대로12길 25-1';
/** 주소변환 실패 시 fallback 좌표(강남구청 인근) */
const DEFAULT_CENTER = { lat: 37.5172363, lng: 127.0473248 };

const KakaoOfficeMap = () => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [mapState, setMapState] = useState(() =>
    KAKAO_APP_KEY ? 'loading' : 'no-key'
  );

  useEffect(() => {
    if (!KAKAO_APP_KEY || !containerRef.current) return undefined;

    let cancelled = false;
    let loadingTimer = null;

    const ensureSdk = () =>
      new Promise((resolve, reject) => {
        const sdkUrl = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${KAKAO_APP_KEY}&autoload=false&libraries=services`;
        const existing = document.querySelector(
          'script[src*="dapi.kakao.com/v2/maps/sdk.js"]'
        );
        if (existing) {
          if (window.kakao?.maps) {
            resolve();
            return;
          }
          existing.addEventListener('load', () => resolve());
          existing.addEventListener('error', () =>
            reject(new Error('kakao-load'))
          );
          return;
        }
        const script = document.createElement('script');
        script.src = sdkUrl;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('kakao-load'));
        document.head.appendChild(script);
      });

    const mountMap = () => {
      const el = containerRef.current;
      if (cancelled || !el) return;
      if (!window.kakao?.maps || !window.kakao.maps.load) {
        setMapState('script-error');
        return;
      }

      window.kakao.maps.load(() => {
        if (cancelled || !containerRef.current) return;
        const defaultLatLng = new window.kakao.maps.LatLng(
          DEFAULT_CENTER.lat,
          DEFAULT_CENTER.lng
        );
        const map = new window.kakao.maps.Map(containerRef.current, {
          center: defaultLatLng,
          level: 3
        });
        mapRef.current = map;

        const openKakaoMapPlace = (lat, lng) => {
          const url = `https://map.kakao.com/link/map/${encodeURIComponent(
            GEOCODE_ADDRESS_KO
          )},${lat},${lng}`;
          window.open(url, '_blank', 'noopener,noreferrer');
        };

        const placeMarker = (position) => {
          const marker = new window.kakao.maps.Marker({ position, map });
          window.kakao.maps.event.addListener(marker, 'click', () => {
            openKakaoMapPlace(position.getLat(), position.getLng());
          });
        };

        const geocoder = window.kakao?.maps?.services?.Geocoder
          ? new window.kakao.maps.services.Geocoder()
          : null;

        if (!geocoder) {
          placeMarker(defaultLatLng);
          setMapState('ready');
          return;
        }

        geocoder.addressSearch(GEOCODE_ADDRESS_KO, (result, status) => {
          if (cancelled || !containerRef.current || !mapRef.current) return;

          if (
            status !== window.kakao.maps.services.Status.OK ||
            !result?.[0]
          ) {
            placeMarker(defaultLatLng);
            setMapState('ready');
            return;
          }

          const { y, x } = result[0];
          const coords = new window.kakao.maps.LatLng(
            parseFloat(y),
            parseFloat(x)
          );
          map.setCenter(coords);
          placeMarker(coords);
          setMapState('ready');
        });
      });
    };

    ensureSdk()
      .then(() => {
        if (!cancelled) mountMap();
      })
      .catch(() => {
        if (!cancelled) setMapState('script-error');
      });

    // 인증/도메인 문제로 maps 객체가 뜨지 않으면 loading에 머물 수 있어 타임아웃 처리
    loadingTimer = setTimeout(() => {
      if (!cancelled) {
        setMapState((prev) => (prev === 'loading' ? 'script-error' : prev));
      }
    }, 8000);

    return () => {
      cancelled = true;
      if (loadingTimer) clearTimeout(loadingTimer);
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (mapState !== 'ready' || !mapRef.current) return undefined;
    const map = mapRef.current;
    const onResize = () => {
      if (window.kakao?.maps) {
        window.kakao.maps.event.trigger(map, 'resize');
      }
    };
    window.addEventListener('resize', onResize);
    requestAnimationFrame(onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [mapState]);

  const fallbackSearchUrl = `https://map.kakao.com/link/search/${encodeURIComponent(
    GEOCODE_ADDRESS_KO
  )}`;

  const fallback = (
    <div className="contact-kakao-map-fallback">
      <p className="contact-kakao-map-message">
        {t('contact.map.loadError')}
      </p>
      <a
        href={fallbackSearchUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="contact-kakao-map-link"
      >
        {t('contact.map.openKakaoMap')}
      </a>
    </div>
  );

  return (
    <div className="contact-kakao-map-wrap">
      <h4 className="contact-kakao-map-title">{t('contact.map.title')}</h4>
      {!KAKAO_APP_KEY && fallback}
      {KAKAO_APP_KEY && (
        <>
          <div className="contact-kakao-map-inner">
            <div
              ref={containerRef}
              className="contact-kakao-map"
              role="region"
              aria-label={t('contact.map.title')}
            />
            {mapState === 'loading' && (
              <div className="contact-kakao-map-loading">
                {t('contact.map.loading')}
              </div>
            )}
          </div>
          {(mapState === 'geocode-error' || mapState === 'script-error') &&
            fallback}
        </>
      )}
    </div>
  );
};

export default KakaoOfficeMap;
